import { Row, Space } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDuplicateInvoice from 'views/invoices/hooks/useDuplicateInvoice';
import useInvoicesFilters from 'views/invoices/invoice-details/useInvoicesFilters';
import { ListPayload } from 'core/http/response';
import { usePartyContext } from '../../PartyProvider';
import { Invoice, ListInvoicesSortParams } from 'store/invoices/types';
import { DashboardInvoicesData, initialInvoicesDashboardData } from 'views/invoices/types';
import { mapInvoiceStatusCountsToDashboardData } from 'views/invoices/utils';
import { hasPermissions } from 'config/roles/hasPermissions';
import useSession from 'hooks/useSession';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { getCustomerInvoicesStatusCounts, listInvoices } from 'store/invoices/client';
import { getTableChangeParams, SortDirection, SortDirections } from 'utils/tableHelpers';
import InvoicesTable from 'views/invoices/dashboard/components/InvoicesTable';
import Button from 'components/new-ui/base/button/Button';
import { StyledTag } from 'components/new-ui/base/tag/styles';
import { StyledPopoverButton } from 'components/new-ui/composed/popover-buttons-group/styles';
import PopoverButtonsGroup from 'components/new-ui/composed/popover-buttons-group/PopoverButtonsGroup';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import useCustomers from 'hooks/useCustomers';
import InvoicesStatusSegmented from 'views/invoices/components/InvoicesStatusSegmented';
import { useExportInvoice } from 'views/invoices/hooks/useExportInvoice';
import { useArchiveInvoice } from 'views/invoices/hooks/useArchiveInvoice';

const InvoicesTab = () => {
    const { userInfo } = useSession();
    const navigate = useNavigate();
    const { customerId, viewOnly } = usePartyContext();
    const { getInvoicesCount } = useCustomers();
    const { duplicateInvoice } = useDuplicateInvoice();
    const { exportInvoice } = useExportInvoice();
    const { archiveInvoice, unarchiveInvoice } = useArchiveInvoice(async () => {
        await getInvoices();
        await getInvoicesDashboardData();
        if (customerId) getInvoicesCount(customerId);
    });

    const {
        queryInputVal,
        tags,
        appliedParams,
        setStatuses,
        setArchived,
        setPage,
        setSize,
        clearFilter,
        clearAll,
        setDate,
        setQueryInputVal,
        setSortState,
    } = useInvoicesFilters();

    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [invoicesResponse, setInvoicesResponse] = useState<ListPayload<Invoice>>();
    const [invoicesCounts, setInvoicesCounts] = useState<DashboardInvoicesData>(initialInvoicesDashboardData);
    const [showBulkExportPopover, setShowBulkExportPopover] = useState(false);

    const { archived, page, size, sort, sortDirection, exactDate } = appliedParams;
    const canUpdateInvoices = hasPermissions(userInfo, 'invoices', 'UPDATE');
    const hasSelected = selectedRowKeys.length > 0;

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys.map((k) => k.toString()));
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const handleBulkExportCsv = () => exportInvoice({ type: 'bulk-csv', invoiceIds: selectedRowKeys });
    const handleBulkExportPdfAsZip = () => exportInvoice({ type: 'bulk-pdf-zip', invoiceIds: selectedRowKeys });

    const handleDownloadPdf = async (invoiceId: string, orderNumber: string) => {
        exportInvoice({ type: 'pdf', invoiceId, orderNumber });
    };

    const handleDuplicateInvoice = async (invoiceId: string) => {
        duplicateInvoice(invoiceId);
    };

    const handleExportCsv = async (invoiceId: string, orderNumber: string) => {
        exportInvoice({ type: 'csv', invoiceId, orderNumber });
    };

    const getInvoices = useCallback(async () => {
        if (!customerId) return;

        try {
            setIsLoadingInvoices(true);
            const data = await listInvoices({ ...appliedParams, customerId });
            setInvoicesResponse(data);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: errorMessage || 'Could not get invoices data',
                placement: 'top',
            });
        } finally {
            setIsLoadingInvoices(false);
        }
    }, [appliedParams, customerId]);

    const getInvoicesDashboardData = useCallback(async () => {
        if (!customerId) {
            return Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Missing customer information.',
                placement: 'top',
            });
        }
        try {
            const data = await getCustomerInvoicesStatusCounts(customerId, !!archived);
            setInvoicesCounts(mapInvoiceStatusCountsToDashboardData(data.list));
        } catch (error) {
            const err = getErrorMessage(error);
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: err || 'Could not get invoices counts from the server.',
                placement: 'top',
            });
        }
    }, [customerId, archived]);

    // Get initial invoices counts
    useEffect(() => {
        getInvoicesDashboardData();
    }, [getInvoicesDashboardData]);

    // Get initial invoices
    useEffect(() => {
        getInvoices();
    }, [getInvoices]);

    return (
        <Space style={{ width: '100%' }} direction="vertical" size={24}>
            <Row justify="space-between">
                <InvoicesStatusSegmented
                    value={appliedParams?.listInvoiceStatus[0] || 'ALL'}
                    counts={invoicesCounts}
                    archived={!!archived}
                    onChange={(val) => {
                        if (val === 'ALL') {
                            setStatuses([]);
                            setPage(0);
                        } else {
                            setStatuses([val]);
                            setPage(0);
                        }
                    }}
                />

                <ArchiveFilter
                    onChange={(val) => setArchived(val === 'active' ? false : true)}
                    value={archived ? 'archived' : 'active'}
                />
            </Row>

            <Row justify="space-between">
                <Space>
                    <SearchBar
                        inputPlaceholder="Search invoices"
                        value={queryInputVal}
                        onSearch={(val) => {
                            setQueryInputVal(val);
                            setPage(0);
                        }}
                        onClear={() => {
                            setQueryInputVal('');
                            setPage(0);
                        }}
                    />
                </Space>
                <Space>
                    <DatePicker
                        value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                        onChange={(val) => {
                            if (!val) {
                                setDate(undefined);
                                return;
                            }
                            setDate(val.format('YYYY-MM-DD'));
                        }}
                    />
                    <PopoverButtonsGroup
                        buttonEl={<Button disabled={!hasSelected}>Export</Button>}
                        open={showBulkExportPopover}
                        onOpenChange={setShowBulkExportPopover}
                    >
                        <StyledPopoverButton
                            onClick={() => {
                                handleBulkExportCsv();
                                setShowBulkExportPopover(false);
                            }}
                        >
                            Export CSV
                        </StyledPopoverButton>
                        <StyledPopoverButton
                            onClick={() => {
                                handleBulkExportPdfAsZip();
                                setShowBulkExportPopover(false);
                            }}
                        >
                            Export PDF
                        </StyledPopoverButton>
                    </PopoverButtonsGroup>
                </Space>
            </Row>

            <Row>
                {tags.map((t) => {
                    return (
                        <StyledTag
                            key={`${t.key}-${t.value}`}
                            closable
                            onClose={(e) => {
                                e.preventDefault();
                                clearFilter(t.key);
                            }}
                        >
                            {t.value}
                        </StyledTag>
                    );
                })}

                {tags?.length > 0 && (
                    <Button type="link" onClick={clearAll}>
                        Clear All Filters
                    </Button>
                )}
            </Row>

            <InvoicesTable
                loading={isLoadingInvoices}
                data={invoicesResponse?.list ?? []}
                showActions={canUpdateInvoices}
                actions={{
                    exportCsv: (invoiceId, orderNumber) => handleExportCsv(invoiceId, orderNumber),
                    downloadPdf: (invoiceId, orderNumber) => handleDownloadPdf(invoiceId, orderNumber),
                    ...(!viewOnly && {
                        archive: (invoiceId) => archiveInvoice(invoiceId),
                        unarchive: (invoiceId) => unarchiveInvoice(invoiceId),
                        duplicate: (invoiceId) => handleDuplicateInvoice(invoiceId),
                    }),
                }}
                pagination={{
                    current: page + 1,
                    pageSize: size,
                    total: invoicesResponse?.total,
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    hideOnSinglePage: true,
                }}
                onChange={(antPagination, _filters, sorter) => {
                    const raw = getTableChangeParams({ pagination: antPagination, sorter });

                    const { page, size, sortParameter, sortDirection } = (raw || {}) as {
                        page?: number;
                        size?: number;
                        sortParameter?: string;
                        sortDirection?: SortDirection;
                    };

                    setPage((p) => (typeof page === 'number' ? page : p));
                    setSize((p) => (typeof size === 'number' ? size : p));

                    if (!sortParameter || !sortDirection) {
                        setSortState({ sort: undefined, sortDirection: undefined });
                    } else {
                        setSortState({
                            sort: sortParameter as ListInvoicesSortParams,
                            sortDirection: sortDirection as SortDirections,
                        });
                    }
                }}
                onRow={(record) => ({
                    onClick: (e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('.no-row-nav')) {
                            return;
                        }
                        navigate(`/invoices/${record.id}`);
                    },
                    style: { cursor: 'pointer' },
                })}
                currentSortDirection={sortDirection}
                currentSortParam={sort}
                rowSelection={canUpdateInvoices && !viewOnly ? rowSelection : undefined}
            />
        </Space>
    );
};

export default InvoicesTab;
